import React, { ClassAttributes, useCallback, useMemo } from 'react'
import { MessageStatusAnnotation } from '@components/MessageTimeIineItem/items/MessageItem/MessageStatusAnnotation'
import { OTWMention } from '@towns-protocol/sdk'
import { MentionPlugin } from '@udecode/plate-mention/react'
import { Box, EditedLabel } from '@ui'
import { clsx } from 'clsx'
import { every, isEqual } from 'lodash-es'
import { Channel, LookupUserFn, useUserLookupContext } from 'use-towns-client'
import { isEmoji } from 'utils/isEmoji'

import * as fieldStyles from '../../ui/components/_internal/Field/Field.css'
import { atoms } from '../../ui/styles/atoms.css'

import { ChannelLinkForDisplay } from './components/ChannelLink'
import { getUserHashMap } from './components/plate-ui/autocomplete/helpers'
import {
    TChannelMentionElement,
    TContractAddressElement,
    TTickerMentionElement,
    TUserIDNameMap,
    TUserMentionElement,
} from './components/plate-ui/autocomplete/types'
import { BlockquoteElementWithoutPlate } from './components/plate-ui/BlockquoteElement'
import { CodeBlockPreview } from './components/plate-ui/CodeBlockPreview'
import { CodeLeaf } from './components/plate-ui/CodeLeaf'
import { ContractAddressElementWithoutPlate } from './components/plate-ui/ContractAddressElement'
import { EmojiElementWithoutPlate } from './components/plate-ui/EmojiElement'
import { LinkWithoutPlate } from './components/plate-ui/LinkElement'
import { ListElement } from './components/plate-ui/ListElement'
import { MentionElementWithoutPlate } from './components/plate-ui/MentionElement'
import { ParagraphWithoutPlate } from './components/plate-ui/ParagraphElement'
import { TickerMentionElementWithoutPlate } from './components/plate-ui/TickerMentionElement'
import { ELEMENT_MENTION_CHANNEL } from './plugins/createChannelPlugin'
import { ELEMENT_CONTRACT_ADDRESS } from './plugins/createContractAddressPlugin'
import { ELEMENT_MENTION_TICKER } from './plugins/createTickerMentionPlugin'
import { getChannelNames, getMentionIds } from './utils/helpers'
import MarkdownToJSX from './utils/MarkdownToJSX'
import { ELEMENT_EDITED } from './utils/remark/remarkEditedAnnotation'
import { ELEMENT_EMOJI } from './utils/remark/remarkEmoji'
import { richText, singleEmojiMessage } from './RichTextEditor.css'

const fieldClassName = clsx([fieldStyles.field, richText])

interface RichTextPreviewProps {
    content: string
    statusAnnotation?: MessageStatusAnnotation
    // Contains the list of mentions in the message we're currently previewing.
    // The users may not be in the space any more or their names may have changed
    mentions?: OTWMention[]
    channels?: Channel[]
    onMentionClick?: (mentionName: string) => void
    onMentionHover?: (element?: HTMLElement, userId?: string) => void
    highlightTerms?: string[]
    lookupUser?: LookupUserFn
}

export const RichTextPreviewInternal = ({
    content,
    statusAnnotation,
    mentions = [],
    channels,
    onMentionClick,
    onMentionHover,
    lookupUser,
}: RichTextPreviewProps) => {
    const _onMentionHover = useCallback(
        (element?: HTMLElement, userId?: string) => {
            if (!onMentionHover) {
                return
            }
            if (userId && element) {
                onMentionHover(element, userId)
            } else {
                onMentionHover(undefined, undefined)
            }
        },
        [onMentionHover],
    )

    const userHashMap: TUserIDNameMap = useMemo(() => {
        return getUserHashMap(mentions)
    }, [mentions])

    const isSingleEmoji = useMemo(() => isEmoji(content), [content])

    const memoizedComponents = useMemo(
        () => ({
            ul: (props: React.PropsWithChildren<ClassAttributes<HTMLUListElement>>) => (
                <ListElement variant="ul">{props.children}</ListElement>
            ),
            ol: (props: React.PropsWithChildren<ClassAttributes<HTMLOListElement>>) => (
                <ListElement variant="ol" {...props}>
                    {props.children}
                </ListElement>
            ),
            li: (props: React.PropsWithChildren<ClassAttributes<HTMLLIElement>>) => (
                <ListElement variant="li">{props.children}</ListElement>
            ),
            lic: (props: React.PropsWithChildren) => (
                <ListElement variant="span">{props.children}</ListElement>
            ),
            p: ParagraphWithoutPlate,
            paragraph: ParagraphWithoutPlate,
            strong: (props: React.PropsWithChildren) => (
                <Box fontWeight="strong" display="inline" as="strong">
                    {props.children}
                </Box>
            ),
            em: (props: React.PropsWithChildren) => (
                <Box fontStyle="italic" display="inline" as="em">
                    {props.children}
                </Box>
            ),
            [ELEMENT_EDITED]: (props: React.PropsWithChildren) => <EditedLabel withMargin />,
            u: (props: React.PropsWithChildren) => (
                <Box textDecoration="underline" display="inline" as="u">
                    {props.children}
                </Box>
            ),
            code: CodeLeaf,
            pre: CodeBlockPreview,
            blockquote: BlockquoteElementWithoutPlate,
            a: LinkWithoutPlate,
            [MentionPlugin.key]: (
                props: React.PropsWithChildren<{ node: TUserMentionElement }>,
            ) => (
                <MentionElementWithoutPlate
                    value={props.node.value}
                    userId={props.node.userId}
                    onMentionHover={_onMentionHover}
                    onMentionClick={onMentionClick}
                />
            ),
            [ELEMENT_MENTION_CHANNEL]: (
                props: React.PropsWithChildren<{ node: TChannelMentionElement }>,
            ) => <ChannelLinkForDisplay channels={channels} channelLabel={props.node.value} />,
            [ELEMENT_MENTION_TICKER]: (
                props: React.PropsWithChildren<{ node: TTickerMentionElement }>,
            ) => <TickerMentionElementWithoutPlate symbol={props.node.value} />,
            [ELEMENT_CONTRACT_ADDRESS]: (
                props: React.PropsWithChildren<{ node: TContractAddressElement }>,
            ) => <ContractAddressElementWithoutPlate address={props.node.address} />,
            [ELEMENT_EMOJI]: (
                props: React.PropsWithChildren<{ node: { emoji: string; value: string } }>,
            ) => <EmojiElementWithoutPlate emoji={props.node.emoji || props.node.value} />,
        }),
        [_onMentionHover, onMentionClick, channels],
    )

    return (
        // this extra <div> prevents the preview from starting up too big,
        // ...yet to find out why this occurs
        <div
            className={atoms({
                color: statusAnnotation === 'not-sent' ? 'error' : undefined,
            })}
        >
            <Box
                as="div"
                className={clsx(fieldClassName, {
                    [singleEmojiMessage]: isSingleEmoji,
                })}
                data-testid={isSingleEmoji ? 'single-emoji-message' : undefined}
            >
                <MarkdownToJSX
                    components={memoizedComponents}
                    isEdited={statusAnnotation === 'edited'}
                    channels={channels}
                    userHashMap={userHashMap}
                    lookupUser={lookupUser as LookupUserFn}
                >
                    {content}
                </MarkdownToJSX>
            </Box>
        </div>
    )
}

const arePropsEqual = (
    prevProps: RichTextPreviewProps,
    nextProps: RichTextPreviewProps,
): boolean => {
    return every(
        [
            isEqual(prevProps.content, nextProps.content),
            isEqual(prevProps.statusAnnotation, nextProps.statusAnnotation),
            isEqual(getMentionIds(prevProps.mentions), getMentionIds(nextProps.mentions)),
            isEqual(getChannelNames(prevProps.channels), getChannelNames(nextProps.channels)),
            isEqual(prevProps.highlightTerms, nextProps.highlightTerms),
        ],
        true,
    )
}

const RichTextPreviewContainer = (props: RichTextPreviewProps) => {
    const { lookupUser } = useUserLookupContext()
    return <RichTextPreviewInternal {...props} lookupUser={lookupUser} />
}

export const RichTextPreview = React.memo(RichTextPreviewContainer, arePropsEqual)
